/**
 * PowerPoint helpers — read selected slide / all slides text via the
 * common Document.getSelectedDataAsync + getFileAsync APIs (PowerPoint
 * still lacks a rich Common.js host like Word/Excel, so we use the
 * lower-level APIs).
 */

export interface PowerPointContext {
  /** Plain text from the currently selected slide(s). */
  selectionText: string;
  /** Index of the selected slide (1-based) or 0 if unknown. */
  selectedSlideIndex: number;
  /** Total slide count when known. */
  slideCount: number;
}

export async function readPowerPointContext(): Promise<PowerPointContext> {
  const selectionText = await getSelectedText();
  const meta = await getSlideMetadata();
  return { selectionText, ...meta };
}

function getSelectedText(): Promise<string> {
  return new Promise((resolve) => {
    Office.context.document.getSelectedDataAsync(
      Office.CoercionType.Text,
      (res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          resolve(typeof res.value === 'string' ? res.value : '');
        } else {
          resolve('');
        }
      },
    );
  });
}

interface SlideMeta {
  selectedSlideIndex: number;
  slideCount: number;
}

function getSlideMetadata(): Promise<SlideMeta> {
  return new Promise((resolve) => {
    // getSelectedDataAsync(SlideRange) returns slide indices in PowerPoint.
    try {
      Office.context.document.getSelectedDataAsync(
        // @ts-expect-error CoercionType.SlideRange is PowerPoint-only and
        // not part of the cross-host CoercionType enum.
        'slideRange',
        (res: Office.AsyncResult<unknown>) => {
          let idx = 0;
          if (res.status === Office.AsyncResultStatus.Succeeded && res.value) {
            const slides = (res.value as { slides?: Array<{ index: number }> }).slides;
            if (slides && slides.length > 0) idx = slides[0].index;
          }
          resolve({ selectedSlideIndex: idx, slideCount: 0 });
        },
      );
    } catch {
      resolve({ selectedSlideIndex: 0, slideCount: 0 });
    }
  });
}

/** Insert text on the active slide as a new text box (best effort). */
export function insertOnActiveSlide(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(text, { coercionType: Office.CoercionType.Text }, (res) => {
      if (res.status === Office.AsyncResultStatus.Succeeded) resolve();
      else reject(new Error(res.error?.message || 'Insert failed'));
    });
  });
}
